<!--
  StorefrontListingPanel - Section 2 of the hi-fi Product editor.

  Pebble 5 of "Product editor (hi-fi redesign)"
  https://trello.com/c/Qlwx8mlT

  Day 8 Item B2 rebuild (2026-06-13 design bundle):
  the section now mounts THREE cards per the locked IA:
    1. Listing            - name, category, short / long description,
                            COMBINED product-image picker (card
                            https://trello.com/c/efG02KCo): the Available-
                            images pool shows images across ALL of the
                            product's colors in one view; each pool tile
                            carries its ORIGIN color and selecting it
                            persists into storefront_images[originColor], so
                            per-color STORAGE + the shopper's per-color
                            display are preserved. The Chosen grid is ONE
                            list sortable across colors (Jake 2026-10-07):
                            each entry carries a global `position`.
                            DECORATED RENDERS (Jake 2026-10-06/07): every
                            selected color gets its own decorated render,
                            30s after the product was last SAVED (or at once
                            when the Stage switches to it), and that render
                            is CHOSEN by default; a distributor's removal
                            sticks. Opening a product renders nothing;
                            "Render all colors" renders at once.
                            Deselecting a color drops it from the listing
                            (render and blanks). Policy lives in
                            useStorefrontColorRenders; see the "Decorated
                            renders" block in the script.
    2. Quantity per shopper - inherited readout + override toggle, with
                              min / max custom inputs when overridden
    3. Visibility         - storefront-visible toggle (mirrors the
                            "Hide from storefront" header pill)

  Field map (see Locked Decisions sec.7 + FIELD-MAP.md):
    - product_name           -> Listing.Name
    - store_category_id      -> Listing.Category (D1-frontend, card
                                https://trello.com/c/a7j1VV6D: entity
                                membership, read-only on the CP GET, written
                                via an IMMEDIATE PATCH to
                                /campaign_products/<id>/category -- NOT the
                                buffered dirty-Map/Save flow. Options come
                                from GET /api/stores/<id>/categories, Home
                                excluded. Supersedes the legacy
                                product_category string + hard-coded
                                store_product_categories list.)
    - short_description      -> Listing.Short description
    - long_description       -> Listing.Long description
    - min_quantity           -> Quantity per shopper, INHERITED min readout
    - max_quantity           -> Quantity per shopper, INHERITED max readout
    - quantity_per_shopper_override (JSONB {min,max} | null)
                             -> Quantity per shopper, OVERRIDE inputs
    - visible (bool)         -> Visibility toggle (header pill also
                                writes the same field via Group A
                                EditorPagehead wiring)

  Out of scope:
    - supplierMeta + setSupplierMeta live on the store and are populated
      by SupplierSkuPanel after a successful meta fetch
    - decoration setup, pricing internals, ColorsPanel selection model
-->
<template>
  <div class="storefront-section">
    <v-card data-testid="storefront-listing-card">
        <v-card-title class="storefront-header">
            <v-icon icon="storefront" class="storefront-header-icon" />
            <div class="storefront-header-text">
                <div
                    data-testid="storefront-card-title"
                    class="storefront-title"
                >
                    Storefront listing
                </div>
                <div
                    data-testid="storefront-card-subtitle"
                    class="storefront-subtitle"
                >
                    Shown to shoppers on the storefront
                </div>
            </div>
        </v-card-title>

        <v-card-text>
            <div class="storefront-fields">
                <v-text-field
                    :model-value="productName"
                    data-testid="storefront-name-input"
                    label="Name"
                    density="compact"
                    variant="outlined"
                    hide-details
                    required
                    class="storefront-name"
                    @update:model-value="onUpdate('product_name', $event)"
                />

                <v-select
                    :model-value="storeCategoryId"
                    data-testid="storefront-category-select"
                    label="Category"
                    :items="categoryItems"
                    item-title="title"
                    item-value="value"
                    density="compact"
                    variant="outlined"
                    hide-details
                    class="storefront-category"
                    style="max-width: 240px"
                    :disabled="categoryPatchInFlight"
                    @update:model-value="onSelectCategory"
                />

                <v-text-field
                    :model-value="shortDescription"
                    data-testid="storefront-short-desc-input"
                    label="Short description"
                    density="compact"
                    variant="outlined"
                    hide-details
                    class="storefront-short-desc"
                    @update:model-value="
                        onUpdate('short_description', $event)
                    "
                />

                <v-textarea
                    :model-value="longDescription"
                    data-testid="storefront-long-desc-textarea"
                    label="Long description"
                    density="compact"
                    variant="outlined"
                    hide-details
                    rows="3"
                    auto-grow
                    class="storefront-long-desc"
                    @update:model-value="
                        onUpdate('long_description', $event)
                    "
                />
            </div>

            <!--
              COMBINED product-image picker (card
              https://trello.com/c/efG02KCo, Jake 2026-07-15). The
              "Available images" pool shows the UNION of images across ALL
              the product's colors by default ("[All]") -- no color-dropdown
              switching required to see another color's images. Each pool
              tile carries its ORIGIN color (per-color SanMar / legacy ->
              that color; Artifi -> its stamped render color; flat SanMar
              -> the editor-focus color, see below) and selecting it
              persists into storefront_images[originColor], so per-color
              STORAGE + the shopper's per-color display stay intact (a Navy
              photo stays Navy's image). The "Chosen" grid is ONE flat list,
              drag-sortable across colors (Jake 2026-10-07): each entry
              carries a GLOBAL `position` and its badge shows it. Writes the
              whole storefront_images map via updateField -> the editor's
              dirty-Map + Save flush (single write model).

              The color dropdown (PR-B, Jake 2026-07-21; scoped to the pool
              only, Jake bug report 2026-07-29) is a REAL FILTER over the
              Available-images POOL ONLY, defaulting to "[All]". The Chosen
              grid never filters -- it always shows every chosen image
              across every color, regardless of the dropdown's value. The
              dropdown is PANEL-LOCAL and decoupled from editor-wide focus:
              it does not move Stage/ColorFilmstrip/DecorationInspector, and
              it is NOT the write-target for origin-less (flat SanMar)
              tiles -- that stays driven by editor-wide focus
              (editorUi.selectedColorSku, see activeColor/activeColorName)
              exactly as before. Flat color-less SanMar images are hidden
              from the pool under a specific filter (no real color to
              match) and only ever show under "[All]".
            -->
            <div
                data-testid="storefront-picker"
                class="storefront-picker"
            >
                <div
                    v-if="pickerColors.length === 0"
                    data-testid="picker-no-colors"
                    class="storefront-picker-empty"
                >
                    Pick a SKU and colors first to choose product images.
                </div>

                <!--
                  "Available images" header + the color FILTER together, so
                  the filter reads as scoped to the pool below it (Jake
                  2026-07-30). Gated on ``pickerColors.length > 0`` (NOT on
                  ``poolTiles.length`` like the pool itself) so the filter
                  never disappears when a specific color yields an empty
                  pool -- otherwise the user would be stranded, unable to
                  switch back to "[All]".
                -->
                <div
                    v-if="pickerColors.length > 0"
                    data-testid="picker-pool-heading"
                    class="storefront-picker-heading"
                >
                    Available images
                    <span class="storefront-picker-heading-hint">
                        images across all colors; click to add
                    </span>
                </div>
                <div
                    v-if="pickerColors.length > 0"
                    class="storefront-picker-color-row"
                >
                    <v-select
                        :model-value="listingColorFilter"
                        data-testid="picker-color-selector"
                        label="Filter by Color"
                        :items="pickerColorItems"
                        density="compact"
                        variant="outlined"
                        hide-details
                        style="max-width: 240px"
                        @update:model-value="onSelectColorFilter"
                    />
                </div>
                <!--
                  Deliberate backfill (Jake 2026-10-06). Opening a product
                  renders nothing, so the colors it was opened with get their
                  decorated previews from this ONE click: every selected color
                  with no preview renders now, under the same cap and cache
                  rules, and lands per the default-include rule (a curated
                  color gets it in the pool only). Shown only when there is
                  something to render.
                -->
                <button
                    v-if="pickerColors.length > 0 && colorRenders.missingColors.length > 0"
                    type="button"
                    data-testid="picker-render-all"
                    class="storefront-picker-render-all"
                    @click="colorRenders.renderAll()"
                >
                    <v-icon icon="auto_awesome" class="storefront-picker-render-all-icon" />
                    Render all colors
                    <span class="storefront-picker-render-all-count">
                        ({{ missingRenderLabel }})
                    </span>
                </button>
                <div
                    v-if="pickerColors.length > 0 && (poolTiles.length > 0 || pendingRenderColors.length > 0 || partialRenderNotes.length > 0)"
                    data-testid="picker-pool"
                    class="storefront-picker-pool"
                >
                    <!--
                      A decorated render on its way for this color (queued or
                      in flight -- Jake 2026-10-06/07). Not a button: there
                      is nothing to pick yet.
                      Reuses the Stage's shimmer so "generating" reads the
                      same everywhere in the editor.
                    -->
                    <!--
                      A render that dropped a placement (#312): the backend
                      composited only some of the product's placements, so
                      the image is withheld rather than offered as the
                      finished one. Retried automatically; "Render all
                      colors" offers it again once retries run out.
                    -->
                    <div
                        v-for="note in partialRenderNotes"
                        :key="`partial ${note.color}`"
                        :data-testid="`picker-render-partial-${note.color}`"
                        :data-origin-color="note.color"
                        class="storefront-picker-partial"
                        role="status"
                    >
                        <strong>{{ note.color }}:</strong>
                        {{ note.message }}
                    </div>
                    <div
                        v-for="color in pendingRenderColors"
                        :key="`pending ${color}`"
                        data-testid="picker-pool-tile-pending"
                        :data-origin-color="color"
                        class="storefront-picker-tile storefront-picker-tile--pending"
                        role="status"
                        aria-busy="true"
                        :aria-label="`Generating the decorated preview for ${color}`"
                        :title="`Generating the decorated preview for ${color}`"
                    >
                        <RenderShimmer class="storefront-picker-tile-shimmer" aria-hidden="true" />
                        <span
                            class="storefront-picker-tile-color"
                            data-testid="picker-tile-color"
                        >{{ color }}</span>
                    </div>
                    <button
                        v-for="tile in poolTiles"
                        :key="`${tile.originColor} ${tile.url}`"
                        type="button"
                        data-testid="picker-pool-tile"
                        :data-source="tile.source"
                        :data-origin-color="tile.originColor"
                        :data-selected="tile.selected ? 'true' : 'false'"
                        class="storefront-picker-tile"
                        :class="{ 'is-selected': tile.selected }"
                        @click="onToggleTile(tile.url, tile.source, tile.originColor)"
                    >
                        <img
                            :src="tile.url"
                            alt="Product image option"
                            :data-placement-count="tile.placements ?? undefined"
                            class="storefront-picker-tile-img"
                            :data-testid="
                                tile.source === 'artifi'
                                    ? `picker-pool-render-img-${tile.originColor}`
                                    : `picker-pool-tile-img-${tile.originColor}-${tile.url}`
                            "
                        />
                        <span
                            class="storefront-picker-tile-color"
                            data-testid="picker-tile-color"
                        >{{ tile.originColor }}</span>
                        <span
                            v-if="tile.selected"
                            class="storefront-picker-tile-order"
                            data-testid="picker-tile-order"
                        >{{ tile.order }}</span>
                    </button>
                </div>
                <!--
                  Empty pool: say WHY, one message per cause the code can
                  know (card #337 / GH-4438; Jake 2026-10-21, copy only --
                  no stand-in images, no blocking). poolEmptyReason is null
                  while supplier metadata is still loading, so nothing is
                  claimed before the answer is known.
                -->
                <div
                    v-else-if="poolEmptyReason"
                    class="storefront-picker-empty"
                    role="status"
                >
                    <p
                        v-if="poolEmptyReason === 'no-colors'"
                        data-testid="picker-pool-empty-no-colors"
                    >
                        Choose at least one color for this product, and its
                        images will show up here.
                    </p>
                    <p
                        v-else-if="poolEmptyReason === 'meta-failed'"
                        data-testid="picker-pool-empty-meta-failed"
                    >
                        We couldn't load {{ poolSupplierLabel }}'s images for
                        this product. Reload the page to try again.
                    </p>
                    <p
                        v-else-if="poolEmptyReason === 'no-supplier-photos'"
                        data-testid="picker-pool-empty-no-supplier-photos"
                    >
                        {{ poolSupplierLabel }} has no photos for this style,
                        so there is nothing to choose from here.
                    </p>
                    <p
                        v-else-if="poolEmptyReason === 'colors-unmatched'"
                        data-testid="picker-pool-empty-colors-unmatched"
                    >
                        {{ poolSupplierLabel }} has photos for this style, but
                        not for the colors you selected. Choose different
                        colors to see them.
                    </p>
                    <p
                        v-else-if="poolEmptyReason === 'filtered'"
                        data-testid="picker-pool-empty-filtered"
                    >
                        No photos for {{ listingColorFilter }}. Choose [All] in
                        the color filter to see every available image.
                    </p>
                </div>

                <!--
                  The Chosen grid: every chosen image, all colors, in GLOBAL
                  display order (Jake 2026-10-07, card
                  https://trello.com/c/Qlwx8mlT -- "I don't want the chosen
                  images to be grouped by color because that prevents being
                  able to sort the images across colors"). ONE flat
                  sortablejs container; a drag moves an image anywhere,
                  across colors, and onReorder rewrites the global
                  `position`s (moveChosenImage) through updateField (single
                  write model). The image keeps its color: storage stays
                  keyed by color for the product page. The card carousel
                  follows the same order. Each tile names its color.
                -->
                <div
                    v-if="lineupItems.length > 0"
                    data-testid="picker-lineup-heading"
                    class="storefront-picker-heading"
                >
                    Chosen (display order)
                    <span class="storefront-picker-heading-hint">
                        drag to reorder; the product card shows them in this
                        order
                    </span>
                </div>
                <div
                    v-if="lineupItems.length > 0"
                    ref="lineupGrid"
                    data-testid="picker-lineup"
                    class="storefront-picker-lineup"
                    :data-color-sequence="lineupColorSequence"
                >
                    <!--
                      :key is the item's STABLE IDENTITY (color + url), NOT
                      its position. onToggleTile keeps (color, url) unique
                      within a color, so this is a safe unique key. Keying on
                      the mutable within-color index made a reorder change
                      every affected key, so Vue DESTROYED + RECREATED the DOM
                      nodes instead of MOVING them -- which detached the nodes
                      the once-bound sortablejs instance tracks (it is not
                      re-init'd on a lineup mutation), so its next onEnd
                      reported stale indices and the reorder no-op'd / reverted
                      with the order badges left stale. Identity keys let Vue
                      MOVE the existing nodes, keeping sortablejs's references
                      live (single-source-of-state: identity is not position).
                    -->
                    <template
                        v-for="(item, i) in lineupItems"
                        :key="`${item.color} ${item.url}`"
                    >
                        <div
                            data-testid="picker-lineup-item"
                            :data-source="item.source"
                            :data-origin-color="item.color"
                            :data-deselected="item.deselected ? 'true' : 'false'"
                            :data-is-default="isDefaultColor(item.color) ? 'true' : 'false'"
                            :data-lineup-index="i"
                            class="storefront-picker-lineup-item"
                            :class="{
                                'is-deselected': item.deselected,
                                'is-default': isDefaultColor(item.color),
                            }"
                        >
                            <span class="storefront-picker-lineup-order">{{
                                i + 1
                            }}</span>
                            <!--
                              De-selected-color badge (Jake, card q7EQ3a0b): this
                              chosen image belongs to a color that is no longer
                              selected in Sizes & Colors. Since Jake 2026-10-06
                              deselecting a color PRUNES it (ColorsPanel), so this
                              now only surfaces data saved before that rule.
                              Visible text (not color-only) for accessibility; the
                              title carries the color name for full context.
                              Positioned as a compact chip in the band directly
                              above the color-name strip (2026-07-29 relayout) so
                              the top controls (order number, remove X) stay
                              clean and the garment image is unobstructed.
                            -->
                            <span
                                v-if="item.deselected"
                                class="storefront-picker-lineup-deselected"
                                data-testid="chosen-image-deselected-badge"
                                :title="`${item.color} is not a selected color`"
                            >
                                <v-icon
                                    icon="warning"
                                    class="storefront-picker-lineup-deselected-icon"
                                />
                                Not a selected color
                            </span>
                            <span
                                class="storefront-picker-lineup-color"
                                data-testid="picker-lineup-color"
                            >{{ item.color }}</span>
                            <button
                                type="button"
                                data-testid="picker-remove"
                                class="storefront-picker-remove"
                                aria-label="Remove image"
                                title="Remove"
                                @click="onRemoveGlobal(i)"
                            >
                                <v-icon icon="close" />
                            </button>
                            <!--
                              "Set as default" (Jake's decision B) -- writes
                              the EXISTING campaign_products.default_image
                              column (a color name), not a new per-image
                              field; see the panel-level doc comment for the
                              reuse-vs-new-field reasoning. @click.stop so it
                              never triggers card drag/selection.
                            -->
                            <button
                                type="button"
                                data-testid="picker-set-default"
                                class="storefront-picker-set-default"
                                :class="{ 'is-active': isDefaultColor(item.color) }"
                                :aria-label="`Set ${item.color} as the default storefront image`"
                                :title="
                                    isDefaultColor(item.color)
                                        ? `${item.color} is the default`
                                        : `Set ${item.color} as default`
                                "
                                @click.stop="onSetDefaultColor(item.color)"
                            >
                                <v-icon
                                    :icon="isDefaultColor(item.color) ? 'star' : 'star_outline'"
                                />
                            </button>
                            <span
                                v-if="isDefaultColor(item.color)"
                                data-testid="picker-is-default-badge"
                                class="storefront-picker-is-default-badge"
                                :class="{ 'stacks-above-deselected': item.deselected }"
                                :title="`${item.color} is the storefront default`"
                            >
                                Default
                            </span>
                            <img
                                :src="item.url"
                                alt="Selected product image"
                                class="storefront-picker-lineup-img"
                                :data-testid="
                                    isCurrentRender(item)
                                        ? `picker-lineup-render-img-${item.color}`
                                        : `picker-lineup-item-img-${item.color}-${item.url}`
                                "
                            />
                        </div>
                    </template>
                </div>
            </div>

            <div class="storefront-image-status">
                <v-icon icon="sync" class="storefront-image-status-icon" />
                <span
                    data-testid="storefront-image-caption"
                    class="storefront-image-status-text"
                >
                    {{ imageCaption }}
                </span>
            </div>
        </v-card-text>
    </v-card>

    <!--
      Day 8 Item B2 - Quantity per shopper card.

      Inherits store-level min/max by default (read-only readout).
      Toggling the override switch writes a JSONB
      `quantity_per_shopper_override: { min, max }`. Clearing the toggle
      writes null so the CP falls back to store-level inheritance.
    -->
    <v-card
        data-testid="storefront-quantity-card"
        class="storefront-followup-card"
    >
      <v-card-title class="storefront-header">
        <v-icon icon="shopping_cart" class="storefront-header-icon" />
        <div class="storefront-header-text">
          <div
              data-testid="storefront-quantity-card-title"
              class="storefront-title"
          >
            Quantity per shopper
          </div>
          <div class="storefront-subtitle">
            How many units a single shopper may order
          </div>
        </div>
      </v-card-title>

      <v-card-text>
        <div
            data-testid="storefront-quantity-inherited"
            class="storefront-qty-inherited"
        >
          <v-icon icon="link" class="storefront-qty-inherited-icon" />
          <span v-if="quantityOverride">
            Overridden for this product
          </span>
          <span v-else>
            <strong>{{ inheritedMin }} - {{ inheritedMax }}</strong>
            per shopper - inherited from the store
          </span>
        </div>

        <div class="storefront-toggle-row">
          <v-switch
              data-testid="storefront-quantity-override-toggle"
              :model-value="!!quantityOverride"
              color="primary"
              density="compact"
              hide-details
              inset
              @update:model-value="onToggleQuantityOverride"
          />
          <div class="storefront-toggle-label">
            <div>Override for this product</div>
            <small>Set a custom min / max just for this product</small>
          </div>
        </div>

        <div
            v-if="quantityOverride"
            data-testid="storefront-quantity-override-inputs"
            class="storefront-qty-override"
        >
          <v-icon icon="tune" class="storefront-qty-override-icon" />
          <v-text-field
              data-testid="storefront-quantity-override-min"
              :model-value="quantityOverride.min"
              label="Min"
              type="number"
              density="compact"
              variant="outlined"
              hide-details
              class="storefront-qty-input"
              @update:model-value="onUpdateOverrideMin"
          />
          <span class="storefront-qty-separator">to</span>
          <v-text-field
              data-testid="storefront-quantity-override-max"
              :model-value="quantityOverride.max"
              label="Max"
              type="number"
              density="compact"
              variant="outlined"
              hide-details
              class="storefront-qty-input"
              @update:model-value="onUpdateOverrideMax"
          />
          <span class="storefront-qty-suffix">units per shopper</span>
        </div>
      </v-card-text>
    </v-card>

    <!--
      Day 8 Item B2 - Visibility card.

      Writes `visible` (bool). The EditorPagehead "Hide from storefront"
      pill (Group A surface) reads/writes the same field so the two
      controls stay in sync via the store. There is no separate
      hide_from_storefront write here - the backend mirrors it from
      `visible` (see api/src/models/campaign_product.py around the
      visibility / hide_from_storefront sync note).
    -->
    <v-card
        data-testid="storefront-visibility-card"
        class="storefront-followup-card"
    >
      <v-card-title class="storefront-header">
        <v-icon icon="visibility" class="storefront-header-icon" />
        <div class="storefront-header-text">
          <div
              data-testid="storefront-visibility-card-title"
              class="storefront-title"
          >
            Visibility
          </div>
          <div class="storefront-subtitle">
            Whether shoppers can find this product on the storefront
          </div>
        </div>
      </v-card-title>

      <v-card-text>
        <div class="storefront-toggle-row">
          <v-switch
              data-testid="storefront-visibility-toggle"
              :model-value="isVisible"
              color="primary"
              density="compact"
              hide-details
              inset
              @update:model-value="onUpdateVisibility"
          />
          <div class="storefront-toggle-label">
            <div data-testid="storefront-visibility-label">
              {{
                  isVisible
                      ? 'Visible on the storefront'
                      : 'Hidden from the storefront'
              }}
            </div>
            <small>
              {{
                  isVisible
                      ? 'Shoppers can find and order this product'
                      : 'Shoppers cannot see or order this product'
              }}
            </small>
          </div>
        </div>

        <div class="storefront-visibility-note">
          Hiding keeps the product configured but removes it from the
          store. You can also toggle this from the
          <strong>Hide from storefront</strong>
          button in the editor header.
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup>
import { describeError } from "@/utils/safeErrorLog";
import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
} from 'vue'
import { Sortable } from 'sortablejs'
import axiosInstance from '@/axios'
import RenderShimmer from '@/components/decoration/RenderShimmer.vue'
import { useProductEditorStore } from '@/stores/productEditor'
import { useProductEditorUiState } from '@/stores/productEditorUiState'
import { isSanmarPlaceholderUrl } from '@/utils/sanmarPlaceholder'
import { getImageBaseUrl } from '@/utils/imageBaseUrl'
import {
    chosenInDisplayOrder,
    moveChosenImage,
    renumberPositions,
} from '@/utils/storefrontImages'
import { useStorefrontColorRendersStore } from '@/stores/storefrontColorRenders'
import { describePreviewWarnings } from '@/components/decoration/previewWarningCopy'

const store = useProductEditorStore()

// The supplier named in the empty-pool message: the product's own, not
// always SanMar (card #350). Falls back to a neutral word when unnamed.
const poolSupplierLabel = computed(() => {
    const name = (store.getEffectiveAttribute?.("supplier_name") ?? "")
        .toString()
        .trim();
    return name || "supplier";
});
const editorUi = useProductEditorUiState()
const colorRenders = useStorefrontColorRendersStore()

// D1-frontend (card https://trello.com/c/a7j1VV6D, pebble D1-frontend):
// the category picker options + write path moved from the legacy
// denormalized ``store_product_categories`` string list to the A1/B1
// category ENTITY (``StoreCategory``). Options are fetched from the B1
// GET (``GET /api/stores/<id>/categories``, the same endpoint the
// Categories & Storefront editor page reads) -- NOT a second bespoke
// list. This is an EXTRA round-trip (unlike the old string list, which
// rode along the CP GET for free), but it is the entity's canonical
// read surface and keeps this picker's options identical to what the
// Categories page shows, with no risk of drift between two separate
// serializations of the same table.
//
// The Home Page category (``is_home``) is EXCLUDED from the assignable
// options: products are not assigned to Home via this picker -- Home
// curation is a separate surface (the Categories page). If this
// exclusion is ever wrong, it must be an explicit product decision, not
// a silent default.
//
// The picker is PICK-ONLY: category CREATION stays on the Categories
// page. A visible "[none]" item maps to null; a product with no
// membership is uncategorized (mirrors the legacy [none] semantics, now
// keyed by entity id rather than name).
const NONE_CATEGORY_ITEM = { title: '[none]', value: null }

// The owning store id -- same fallback chain EditorPagehead uses
// (``store_id`` post PR-5b rename, ``campaign_id`` for any stale cached
// payload). Needed to fetch this store's entity categories.
const owningStoreId = computed(() => {
    const attrs = store.serverState?.data?.attributes
    if (!attrs) return null
    return attrs.store_id ?? attrs.campaign_id ?? null
})

// Fetched entity categories for the owning store, Home excluded. Starts
// empty (picker shows [none] only) until the GET resolves; a fetch
// failure is swallowed the same way (fail toward an empty, safe picker
// rather than blocking the rest of the editor -- design-philosophy #5).
const storeCategories = ref([])

async function fetchStoreCategories() {
    const storeId = owningStoreId.value
    if (storeId == null) {
        storeCategories.value = []
        return
    }
    try {
        const resp = await axiosInstance.get(`/api/stores/${storeId}/categories`)
        const list = Array.isArray(resp?.data) ? resp.data : []
        storeCategories.value = list.filter((c) => !c?.is_home)
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[StorefrontListingPanel] category fetch failed', describeError(err))
        storeCategories.value = []
    }
}

onMounted(fetchStoreCategories)
// Re-fetch if the owning store resolves after mount (serverState loads
// asynchronously; the store id is not always known on first render).
watch(owningStoreId, (next, prev) => {
    if (next != null && next !== prev) fetchStoreCategories()
})

const categoryItems = computed(() => [
    NONE_CATEGORY_ITEM,
    ...storeCategories.value.map((c) => ({
        title: c.name,
        value: c.id,
    })),
])

// The product's CURRENT category ENTITY membership -- rides along the CP
// GET as a read-only ``store_category_id`` attribute (D1 Part A; see
// CampaignProduct.to_dict / _owning_store_category_id). Read via
// getEffectiveAttribute for consistency with the rest of the panel, but
// this field is never written into the dirty Map (see onSelectCategory) --
// the immediate PATCH below is the only writer, mirroring
// setHideFromStorefront's write model (one write model per surface,
// design-philosophy #6: the D1 endpoint is atomic and per-product, so it
// does not belong behind the buffered Save button).
const storeCategoryId = computed(
    () => store.getEffectiveAttribute('store_category_id') ?? null,
)

const categoryPatchInFlight = ref(false)

// Set (or clear) this product's category via the D1 membership-write
// endpoint. Mirrors EditorPagehead.toggleHide: an immediate, atomic
// server round-trip, not a buffered dirty-Map edit -- there is no
// "unsaved category change" state for the user to lose track of. On
// success, landing the new value onto serverState (via
// store.setStoreCategoryId) makes the picker reflect the persisted value
// with no extra fetch, the same way setHideFromStorefront does for the
// visibility pill.
async function onSelectCategory(value) {
    const cpId = store.campaignProductId
    if (cpId == null) return
    if (categoryPatchInFlight.value) return
    categoryPatchInFlight.value = true
    try {
        await axiosInstance.patch(`/api/campaign_products/${cpId}/category`, {
            store_category_id: value,
        })
        if (typeof store.setStoreCategoryId === 'function') {
            store.setStoreCategoryId(value)
        }
    } catch (err) {
        // Swallow here -- surfacing a toast lives one layer up (mirrors
        // EditorPagehead.toggleHide). The bound value stays at whatever
        // serverState already holds, so the select does not silently
        // show an unpersisted change.
        // eslint-disable-next-line no-console
        console.warn('[StorefrontListingPanel] category PATCH failed', describeError(err))
    } finally {
        categoryPatchInFlight.value = false
    }
}

// Read EFFECTIVE attribute values (dirty if edited, else server) so the
// v-text-field / v-select / v-textarea reflect the user's in-flight edit
// the moment they make it. Reading serverState directly caused the
// user-reported Category bug (2026-05-18): the select snapped back to
// the old value because the computed never saw the dirty Map entry.
// Writes still go through store.updateField, which handles dirty
// tracking and no-op detection.
const productName = computed(
    () => store.getEffectiveAttribute('product_name') ?? '',
)
const shortDescription = computed(
    () => store.getEffectiveAttribute('short_description') ?? '',
)
const longDescription = computed(
    () => store.getEffectiveAttribute('long_description') ?? '',
)

function onUpdate(field, value) {
    store.updateField(field, value)
}

// ============================================================
// Product-image picker slice 1 (card https://trello.com/c/efG02KCo).
//
// Per-color getMediaContent wiring (follow-on to PRs #2013/#2015,
// docs/planning/2026-07-10-sanmar-getmediacontent-image-source.md):
// the pool is sourced PER COLOR from supplierMeta.image_urls_by_color
// when the backend has real getMediaContent images for the active
// color. supplierMeta.image_urls (the flat, per-style list shared
// across colors) remains the FALLBACK -- older responses, EPDD-sourced
// SKUs, or a color with no media all still get a non-empty pool where
// the flat list would have shown something (never worse than the
// pre-existing slice-1 behavior). The builder multi-selects PER COLOR;
// selections persist into storefront_images[color] via updateField (the
// editor's dirty-Map + Save flush). List order = display order,
// preserved as click order.
// ============================================================

// Colors the builder can edit -- resolved from selected_color_skus against
// supplierMeta.colors (same source ColorFilmstrip uses). Each row carries
// a color_sku and a display name; storefront_images is keyed by the color
// NAME (matching the legacy images map + the shopper card's primary
// attribute value).
const pickerColors = computed(() => {
    const rawSkus = store.getEffectiveAttribute?.('selected_color_skus') ?? []
    const skus = [...new Set(rawSkus)]
    if (!skus.length) return []
    const skuSet = new Set(skus)
    const all = store.supplierMeta?.colors ?? []
    const bySku = new Map(all.map((c) => [c.color_sku, c]))
    return skus.map((sku) => {
        const hit = bySku.get(sku)
        return {
            color_sku: sku,
            name: hit?.name ?? sku,
        }
    })
})

// Product Listing color FILTER (card https://trello.com/c/efG02KCo,
// Jake 2026-07-21, PR-B). The sentinel for "no filter" -- ``null`` -- is
// never a real color name and must never be treated as one: no
// storefront_images[LISTING_ALL_COLORS] write. Guarded everywhere a real
// color is required (onToggleTile). Renders are keyed by real color names
// in the storefrontColorRenders store, so the sentinel can never name one.
const LISTING_ALL_COLORS = null

// PANEL-LOCAL filter state (Jake's decision, 2026-07-21): this is
// intentionally NOT editorUi.selectedColorSku. Filtering the Product
// Listing pool/lineup must never move the Stage / ColorFilmstrip /
// DecorationInspector focus -- those stay driven by activeColor below.
// Defaults to "[All]" (no filter), matching today's union-across-colors
// behavior out of the box.
const listingColorFilter = ref(LISTING_ALL_COLORS)

// "[All]" is always the first, default option, ahead of the per-color
// options (unchanged shape/order otherwise).
const pickerColorItems = computed(() => [
    { title: '[All]', value: LISTING_ALL_COLORS },
    ...pickerColors.value.map((c) => ({ title: c.name, value: c.name })),
])

function onSelectColorFilter(value) {
    listingColorFilter.value = value
}

// The WRITE-TARGET color (sku + name) -- separate from the filter above.
// This is where a clicked origin-less (flat SanMar) tile gets filed. (It no
// longer decides which color Artifi renders for: every selected color has
// its own render since Jake 2026-10-06.)
// Unchanged from pre-PR-B behavior: driven by the editor-wide
// selectedColorSku (shared with ColorFilmstrip/Stage), falling back to
// the first available color so it is never empty when colors exist. The
// Product Listing filter (above) no longer writes to this -- the two
// concepts are now fully decoupled.
const activeColor = computed(() => {
    const colors = pickerColors.value
    if (!colors.length) return null
    const sku = editorUi.selectedColorSku
    const hit = colors.find((c) => c.color_sku === sku)
    return hit ?? colors[0]
})

const activeColorName = computed(() => activeColor.value?.name ?? null)

// WHY the Available images pool is empty -- drives the empty-state copy only
// (never the pool itself). Ordered most-fundamental first; null while the
// supplier metadata is still loading (no claim before the answer exists).
const poolEmptyReason = computed(() => {
    if (!pickerColors.value.length) return 'no-colors'
    if (store.supplierMetaFailed) return 'meta-failed'
    const meta = store.supplierMeta
    if (!meta) return store.supplierMetaLoading ? null : 'meta-failed'
    if (allPoolTiles.value.length > 0) return 'filtered'
    const byColor = meta.image_urls_by_color
    const supplierHasPhotos =
        flatSanmarPool.value.length > 0
        || (byColor && typeof byColor === 'object'
            && Object.values(byColor).some((urls) => cleanUrls(urls).length > 0))
    return supplierHasPhotos ? 'colors-unmatched' : 'no-supplier-photos'
})

// Bug #68 -- filter SanMar's "no image" placeholder so it never becomes
// a selectable pool tile. Backend filters at ingest; this is
// defense-in-depth for any pre-existing stored placeholder URL.
function realUrlOrNull(url) {
    if (!url) return null
    return isSanmarPlaceholderUrl(url) ? null : url
}

// Placeholder-filter + de-dupe a raw URL list, preserving order.
function cleanUrls(urls) {
    const seen = new Set()
    const out = []
    for (const raw of urls ?? []) {
        const url = realUrlOrNull(raw)
        if (url && !seen.has(url)) {
            seen.add(url)
            out.push(url)
        }
    }
    return out
}

// The flat, per-style SanMar pool (fallback source) -- placeholder
// -filtered, de-duped, order preserved.
const flatSanmarPool = computed(() =>
    cleanUrls(store.supplierMeta?.image_urls),
)

// KEY-MATCH: image_urls_by_color is keyed by the FULL SanMar display name
// -- the SAME string as each color row's `color_sku`. getMediaContent
// itself labels some colors (notably the heathers: 'Ath Heather',
// 'AthlHthr', ...) by their ABBREVIATED mainframe names, but the backend
// now folds those to the full display name via color_converter.csv when it
// builds the map (see api/src/endpoints/sanmar_feed.py
// _rekey_media_pool_to_full_names), so the active color's color_sku is the
// primary lookup key here and resolves for heathers too. The display name
// is a secondary exact-match key for safety; a case-insensitive pass over
// both is a last-resort fallback so a casing mismatch never causes a
// silent miss when the key is genuinely present under a different case.
function lookupByColorKey(map, color) {
    if (!map || typeof map !== 'object' || !color) return null
    if (Object.prototype.hasOwnProperty.call(map, color.color_sku)) {
        return map[color.color_sku]
    }
    if (
        color.name
        && Object.prototype.hasOwnProperty.call(map, color.name)
    ) {
        return map[color.name]
    }
    const wantedSku = (color.color_sku ?? '').toLowerCase()
    const wantedName = (color.name ?? '').toLowerCase()
    for (const key of Object.keys(map)) {
        const lower = key.toLowerCase()
        if (lower === wantedSku || (wantedName && lower === wantedName)) {
            return map[key]
        }
    }
    return null
}

// Legacy image source (2026-07-10, Jake -- store 6 product 39 CO-VIS-500).
// The CP's legacy `images` column maps a color NAME to a single S3
// uuid/filename (e.g. {"Black": "seed-black.jpg"}). Non-SanMar products
// (empty SanMar pool) carry seeded product photos ONLY here, so the picker
// must surface them as choosable tiles for their color. Resolve the bare
// ref to a URL the same way ProductCard's getImage() does: pass an absolute
// http(s) URL through untouched, else prefix the S3 image base. Returns
// null when the active color has no legacy image.
function resolveLegacyRef(ref) {
    if (typeof ref !== 'string' || ref === '') return null
    if (/^https?:\/\//i.test(ref)) return ref
    return `${getImageBaseUrl()}${ref}`
}

// Legacy image (resolved URL) for a GIVEN color name, or null.
function legacyUrlForColor(colorName) {
    if (!colorName) return null
    const images = store.getEffectiveAttribute?.('images') ?? {}
    return resolveLegacyRef(images?.[colorName])
}

// The persisted per-color selection (dirty-aware). Shape:
// { color: [{source, url}, ...] }.
const storefrontImages = computed(
    () => store.getEffectiveAttribute?.('storefront_images') ?? {},
)

// ------------------------------------------------------------------
// GLOBAL chosen sequence. The "Chosen" grid and the order badges span ALL
// colors, numbered contiguously, in GLOBAL display order (Jake 2026-10-07,
// card https://trello.com/c/Qlwx8mlT): each entry's `position`, which the
// distributor sets by dragging across colors. Data saved before positions
// existed keeps the order it always had -- selected-color order (pickerColors),
// then within-color order -- until the first write normalizes it. The rules
// live in chosenInDisplayOrder (utils/storefrontImages.js), shared with the
// product card carousel.
//
// Storage stays PER COLOR. Each item carries its color and its index in that
// color's stored list so remove can address the right slot.
//
// DE-SELECTED-color surfacing (Jake, card q7EQ3a0b). Since Jake 2026-10-06
// ColorsPanel prunes a deselected color's key, so a chosen image of a color
// that is not selected only comes from data saved before that rule. It is
// surfaced, flagged ``deselected: true`` so the template can badge it --
// never pruned here, on read.
// ------------------------------------------------------------------

// Selected color NAMES in picker order: the default order of position-less
// data, passed to every global-order helper.
const pickerColorNames = computed(() => pickerColors.value.map((c) => c.name))

const globalChosenList = computed(() => {
    const selected = new Set(pickerColorNames.value)
    return chosenInDisplayOrder(storefrontImages.value, pickerColorNames.value).map(
        ({ color, entry, withinColorIndex }, i) => ({
            color,
            source: entry.source ?? 'sanmar',
            url: entry.url,
            withinColorIndex,
            deselected: !selected.has(color),
            globalOrder: i + 1,
        }),
    )
})

// (color -> url -> 1-based GLOBAL order badge) for pool-tile lookup. A pool
// tile that is selected shows its GLOBAL index, matching its Chosen card.
const globalOrderByColorUrl = computed(() => {
    const m = new Map()
    for (const item of globalChosenList.value) {
        m.set(`${item.color} ${item.url}`, item.globalOrder)
    }
    return m
})

// ============================================================
// Decorated renders -- one for EVERY selected color (Jake 2026-10-06, card
// https://trello.com/c/Qlwx8mlT).
//
// SUPERSEDES slice 2's "render ONE color, on focus" guardrail. Every
// selected color now gets its own decorated render, 30s after the product was
// last SAVED (Jake 2026-10-07; each save restarts it), or at once when the
// Stage switches to a color that needs one, and the render is CHOSEN by
// default unless the color is already curated (removal respected). OPENING a
// product renders nothing; the "Render all colors" button renders every
// missing color at once. Each render is also recorded for the product page.
// The policy -- save debounce, cache, 2-in-flight cap, default-include,
// staleness, the no-placement gate --
// lives in useStorefrontColorRenders, which ProductEditor.vue mounts for the
// editor's whole lifetime (colors are picked on a different tab, so the
// countdown cannot live in this panel). This panel only READS the results
// from the storefrontColorRenders store:
//
//   - ready   -> an 'artifi' pool tile under that color, Artifi-led.
//   - pending -> a non-selectable shimmer tile, so the builder can see a
//                render is coming for that color.
//   - failed / absent -> nothing. FAIL TOWARD THE RECORD (unchanged): no
//                bound artwork, an unrouted color, or a failed render never
//                produces a broken or wrong-color tile.
// ============================================================

// Ready renders + pending colors, in picker-color order, for selected colors
// only (a render left over for a deselected color never reaches the pool).
const colorRenderStates = computed(() =>
    pickerColors.value
        .map((color) => ({ color: color.name, state: colorRenders.renderFor(color.name) }))
        .filter(({ state }) => state != null),
)

// True when a Chosen entry IS its color's current render. Drives the stable
// per-color automation testid (picker-lineup-render-img-<color>); an older
// render of the same color keeps its url-keyed testid, so the anchor stays
// unique.
function isCurrentRender(item) {
    const state = colorRenders.renderFor(item.color)
    return state?.status === 'ready' && state.url === item.url
}

// "2 colors without a preview" for the backfill button.
const missingRenderLabel = computed(() => {
    const n = colorRenders.missingColors.length
    return `${n} ${n === 1 ? 'color' : 'colors'} without a preview`
})

// Colors whose latest render dropped a placement (#312), with distributor
// copy built from the backend's warning codes. Same color filter as the pool.
const partialRenderNotes = computed(() => {
    const filter = listingColorFilter.value
    return colorRenderStates.value
        .filter(({ state }) => state.status === 'partial')
        .filter(({ color }) => filter === LISTING_ALL_COLORS || color === filter)
        .map(({ color, state }) => ({
            color,
            message:
                (describePreviewWarnings(state.dropped)?.message ??
                    'Some placements could not be shown on this preview yet.') +
                ' We try again on our own. If it stays like this, re-upload that placement\'s artwork or contact ThreadFlow support.',
        }))
})

const pendingRenderColors = computed(() => {
    const filter = listingColorFilter.value
    return colorRenderStates.value
        .filter(({ state }) => state.status === 'pending')
        .map(({ color }) => color)
        .filter((color) => filter === LISTING_ALL_COLORS || color === filter)
})

// ------------------------------------------------------------------
// COMBINED pool tiles (Jake 2026-07-15). The pool is the UNION of every
// tile-producing source across EVERY color -- the builder picks from all
// colors in one view, no dropdown switching. Each tile carries its ORIGIN
// color; selecting it persists into storefront_images[originColor].
//
// ASSOCIATION RULE (origin color per source):
//   - per-color SanMar (image_urls_by_color[C]) -> origin C
//   - legacy images[C]                           -> origin C
//   - FLAT SanMar (image_urls, no per-color origin) -> origin = ACTIVE
//     color (a shared, origin-less photo attaches to the builder's current
//     context so it lands somewhere sensible and definite)
//   - Artifi decorated render (one per selected color, Jake 2026-10-06)
//     -> origin = the color it was rendered for
//
// De-dup is BY (originColor, url): the same photo can legitimately appear
// once per its own color (a Navy shot and a Red shot are distinct tiles),
// but a color never lists the same url twice. Within one color, FIRST-SEEN
// source wins (Artifi, then SanMar, then legacy) -- so an Artifi-rendered
// url that also shows in the SanMar pool keeps 'artifi'.
//
// `poolSources` lists (originColor, source, urls, isFlat) contributions in
// badge order (Artifi-led, one render per color, then per-color SanMar in
// picker-color order, then legacy). A new source (distributor uploads) is a
// one-line addition; the merge/dedup/tile-build below is source-agnostic.
//
// `isFlat` (Jake 2026-07-21, PR-B color filter): true ONLY for the flat
// SanMar fallback contribution, which has no TRUE per-color origin -- its
// originColor is a borrowed write-target (the active color), not a real
// association. The filter below hides isFlat tiles under a SPECIFIC color
// filter (they have no real color to match) while still showing them under
// "[All]" (today's behavior, unchanged). Per-color SanMar, legacy, and
// Artifi entries all carry a REAL origin color and are never flagged isFlat.
// ------------------------------------------------------------------
const poolSources = computed(() => {
    const active = activeColorName.value
    const sources = []

    // Artifi first ("Artifi-led") -- each selected color's own decorated
    // render, associated with that color.
    for (const { color, state } of colorRenderStates.value) {
        if (state.status === 'ready' && state.url) {
            sources.push({
                originColor: color,
                source: 'artifi',
                urls: [state.url],
                placements: state.placements ?? null,
            })
        }
    }

    // Per-color SanMar pools for EVERY color, in picker-color order. When a
    // color has no per-color list, sanmarPoolForColor falls back to the flat
    // list -- but the flat list has no true per-color origin, so we only
    // attach the flat fallback to the ACTIVE color (below), not to every
    // color (which would duplicate the shared flat set under each color).
    const byColor = store.supplierMeta?.image_urls_by_color
    for (const color of pickerColors.value) {
        const perColorUrls = lookupByColorKey(byColor, color)
        if (Array.isArray(perColorUrls) && perColorUrls.length > 0) {
            sources.push({
                originColor: color.name,
                source: 'sanmar',
                urls: cleanUrls(perColorUrls),
            })
        }
    }

    // Flat SanMar fallback -> the ACTIVE color only. This fires when the
    // active color has no per-color list (older responses / EPDD SKUs);
    // the shared flat set attaches to the builder's current color context.
    // Flagged isFlat: true -- these images have no real per-color origin,
    // so the Product Listing color filter hides them under a specific
    // color (they only ever show under "[All]").
    if (active) {
        const activePerColor = lookupByColorKey(byColor, activeColor.value)
        const activeHasPerColor =
            Array.isArray(activePerColor) && activePerColor.length > 0
        if (!activeHasPerColor) {
            sources.push({
                originColor: active,
                source: 'sanmar',
                urls: flatSanmarPool.value,
                isFlat: true,
            })
        }
    }

    // Legacy CP images[C] photo for EVERY color (seeded product images for
    // non-SanMar products). Persists as 'sanmar' (validator allowlist) --
    // see persistedSource.
    for (const color of pickerColors.value) {
        const url = legacyUrlForColor(color.name)
        if (url) {
            sources.push({
                originColor: color.name,
                source: 'legacy',
                urls: [url],
            })
        }
    }

    return sources
})

// The FULL (unfiltered) pool -- union across all colors, exactly today's
// behavior. This is what "[All]" shows, and is also the source for
// pool-tile order badges (globalOrderByColorUrl), which stay stable
// regardless of the listing filter.
const allPoolTiles = computed(() => {
    // De-dup by (color, url); within a color, first-seen source wins.
    const seen = new Set()
    const tiles = []
    for (const { originColor, source, urls, isFlat, placements } of poolSources.value) {
        for (const url of urls ?? []) {
            if (!url) continue
            const key = `${originColor} ${url}`
            if (seen.has(key)) continue
            seen.add(key)
            const order = globalOrderByColorUrl.value.get(key) ?? null
            tiles.push({
                url,
                source,
                originColor,
                selected: order != null,
                order,
                isFlat: Boolean(isFlat),
                placements: placements ?? null,
            })
        }
    }
    return tiles
})

// Product Listing color FILTER applied to the pool (Jake 2026-07-21,
// PR-B). "[All]" (LISTING_ALL_COLORS) is the union, unchanged from today.
// A specific color filters to ONLY that color's real-origin images:
// per-color SanMar/legacy tiles whose originColor matches, and an Artifi
// tile whose stamped color matches. FLAT color-less SanMar images
// (isFlat: true) are HIDDEN under a specific filter -- they have no real
// color to match, so they only ever appear under "[All]" (Jake's explicit
// decision: purest filter semantics over a denser sparse-pool fallback).
const poolTiles = computed(() => {
    const filter = listingColorFilter.value
    if (filter === LISTING_ALL_COLORS) return allPoolTiles.value
    return allPoolTiles.value.filter(
        (tile) => !tile.isFlat && tile.originColor === filter,
    )
})

// Map a POOL tile's display source to the source it persists as. Only a
// tile being ADDED goes through here, and the pool offers only 'artifi',
// 'sanmar' and 'legacy' tiles; a 'legacy' tile persists as 'sanmar' (the
// shopper card already treats the legacy `images` map as the
// supplier-image fallback, so 'sanmar' is the honest closest value).
// Entries already stored -- including the backend-only 'local' renders and
// the distributor's own 'upload' previews (card #243, which carry a ledger
// key) -- are never re-mapped: every other write spreads them unchanged.
const VALIDATOR_ALLOWED_SOURCES = new Set(['sanmar', 'artifi'])

function persistedSource(displaySource) {
    return VALIDATOR_ALLOWED_SOURCES.has(displaySource)
        ? displaySource
        : 'sanmar'
}

// Toggle a pool image into/out of its ORIGIN color's selection (combined
// picking, Jake 2026-07-15). The origin color -- not the active dropdown
// color -- is the write target, so a Navy photo picked from the combined
// pool lands in storefront_images.Navy and stays Navy's image on the
// shopper card. Adding appends at the END of the global order (click order
// = display order) with the tile's own source ('sanmar'/'artifi'); removing
// drops it and the rest keep their relative order. Either way positions are
// renumbered contiguously. Writes the WHOLE storefront_images map back
// through updateField (single write model).
//
// Each persisted entry is also stamped with `color` (Jake 2026-07-21,
// Artifi color-stamp PR-A): it is always the SAME string as `color` below
// (the write-target map key), so stamping it is a durable, uniform
// record of which color the entry belongs to -- most load-bearing for the
// Artifi pool tile, which is otherwise the one entry shape with no other
// way to recover its render color once stored. This does not change the
// write-target/map-key logic itself (unchanged in this PR).
function onToggleTile(url, source = 'sanmar', originColor = null) {
    // originColor always wins when present -- it comes from a real pool
    // tile's own tagged color (per-color SanMar / legacy / Artifi), never
    // the listing filter. The activeColorName fallback (editor-focus write
    // target, decoupled from the filter) covers the one case where a tile
    // has no origin of its own to fall back on. Guard against the "[All]"
    // filter sentinel ever leaking in here -- it is never a real color.
    const color = originColor ?? activeColorName.value
    if (!color || color === LISTING_ALL_COLORS) return

    const current = storefrontImages.value ?? {}
    const existing = Array.isArray(current[color]) ? current[color] : []
    const idx = existing.findIndex((e) => e && e.url === url)

    let nextEntries
    if (idx >= 0) {
        nextEntries = existing.filter((_, i) => i !== idx)
    } else {
        // Map the display source to a validator-allowed source
        // ('legacy' -> 'sanmar') before persisting.
        nextEntries = [
            ...existing,
            { source: persistedSource(source), url, color },
        ]
    }

    const next = renumberPositions(
        { ...current, [color]: nextEntries },
        pickerColorNames.value,
    )
    store.updateField('storefront_images', next)
}

// The Chosen lineup spans ALL colors (Jake 2026-07-15) under "[All]", in
// the global display order (see globalChosenList). Each card carries its
// color + within-color index (so remove writes back to the right color
// list) and its source badge. Malformed entries are dropped upstream.
//
// Product Listing color FILTER (Jake 2026-07-21, PR-B) applies ONLY to
// the Available-images POOL (poolTiles above), never to this lineup
// (Jake bug report, 2026-07-29 -- PR-B over-applied the filter here).
// The Chosen lineup ALWAYS shows every chosen image across every color,
// regardless of the filter selection, so lineupItems is simply an alias
// for the unfiltered globalChosenList. onReorder/onRemoveGlobal below
// index into THIS array, which is also exactly what the template
// v-for renders, so render order / index / write-back all agree even
// while a pool filter is active.
const lineupItems = computed(() => globalChosenList.value)

// The grid's color sequence, for UI automation: the runner reads it to
// assert the saved cross-color order (selectors are testid-only, so the
// order of identical item testids is otherwise invisible to it).
const lineupColorSequence = computed(() =>
    lineupItems.value.map((item) => item.color).join(','),
)

// Drag-reorder. sortablejs owns the drag gesture on the grid DOM; on drop it
// hands us the old/new index over the RENDERED flat grid (lineupItems).
// onReorder is the single source of truth for the write: moveChosenImage
// moves the image anywhere in the GLOBAL order -- across colors -- and
// rewrites every `position` (the image keeps its color; storage stays keyed
// by color, each list sorted by position). It returns the SAME map on a
// no-op so an accidental no-move drag never dirties the store. The store
// re-renders lineupItems from source-of-truth, so the DOM sortablejs
// reordered and the Vue-rendered list converge.
//
// SUPERSEDES the within-color-only reorder and its cross-color refusal
// (Jake's earlier decision A2): Jake 2026-10-07, grouping by color "is not
// the intended design".
function onReorder(fromIdx, toIdx) {
    const current = storefrontImages.value ?? {}
    const next = moveChosenImage(current, pickerColorNames.value, fromIdx, toIdx)
    if (next === current) return
    store.updateField('storefront_images', next)
}

// "Set as default" (Jake's decision B). REUSES the existing, already-
// validated campaign_products.default_image column -- a color NAME (see
// CampaignProduct.validate_default_image) -- rather than inventing a new
// per-image field. Per-color is sufficient for Jake's reported case
// ("make Graphite the default"): the backend hero/thumbnail resolver
// (api/src/endpoints/storefront_products.py _resolve_storefront_images_url,
// + the M2 canonical resolver's resolve_preview_image_from_storefront_images)
// was fixed in this same change to PREFER default_image's color over raw
// storefront_images dict/JSONB order (Postgres does not preserve JSONB key
// insertion order -- default_image was previously the only reliable signal
// available but the resolver never read it). Written through the SAME
// buffered dirty-Map updateField the rest of this panel uses (single write
// model) -- Save flushes it in the same PATCH as every other field.
const defaultImageColor = computed(
    () => store.getEffectiveAttribute?.('default_image') ?? null,
)

function isDefaultColor(color) {
    return Boolean(color) && defaultImageColor.value === color
}

function onSetDefaultColor(color) {
    if (!color) return
    store.updateField('default_image', color)
}

// The Sortable instance is bound to the grid container DOM element. We
// (re)create it whenever the container appears and destroy it on unmount
// to avoid leaking listeners. Because onReorder immediately re-renders
// the list from the store, the DOM order sortablejs produced is authored
// back into source-of-truth on the same tick.
const lineupGrid = ref(null)
let sortableInstance = null

function destroySortable() {
    if (sortableInstance) {
        sortableInstance.destroy()
        sortableInstance = null
    }
}

function initSortable() {
    destroySortable()
    const el = lineupGrid.value
    if (!el) return
    sortableInstance = Sortable.create(el, {
        animation: 150,
        draggable: '[data-testid="picker-lineup-item"]',
        // The remove (X) button must stay clickable, not start a drag.
        filter: '[data-testid="picker-remove"]',
        preventOnFilter: false,
        // Drag-ghost fix (Jake 2026-07-15): read as "picking up" ONE card,
        // not duplicating it. sortablejs classes:
        //   ghostClass  -> the placeholder left in the source slot (we style
        //                  it as a faint dashed drop-target, clearly NOT a
        //                  second copy of the image);
        //   chosenClass -> the element being picked up (lifted look);
        //   dragClass   -> the element following the cursor.
        // We do NOT use forceFallback/fallbackOnBody -- the native drag image
        // plus a placeholder-styled ghost is the single-copy behavior Jake
        // wants; a fallback clone was the source of the "two copies" look.
        ghostClass: 'picker-drag-ghost',
        chosenClass: 'picker-drag-chosen',
        dragClass: 'picker-drag-active',
        onEnd({ oldIndex, newIndex }) {
            onReorder(oldIndex, newIndex)
        },
    })
}

// The grid element only exists while a color has a non-empty lineup
// (v-if). Watch that condition and (re)bind Sortable to the freshly
// mounted container. nextTick ensures the ref is populated after the
// v-if flips true. lineupItems mutations that keep the container mounted
// do NOT need a re-init -- Sortable tracks the same element and Vue
// re-renders children from the store, which is the source of truth.
watch(
    () => Boolean(activeColorName.value) && lineupItems.value.length > 0,
    async (hasLineup) => {
        if (hasLineup) {
            await nextTick()
            initSortable()
        } else {
            destroySortable()
        }
    },
    { immediate: true },
)

onBeforeUnmount(() => {
    destroySortable()
})

defineExpose({ onReorder })

// Remove a chosen image from the RENDERED lineup by its index (the v-for
// index over lineupItems, which always spans every color -- the Product
// Listing filter never narrows it). The index maps to a (color,
// within-color index) via lineupItems, so the removal drops exactly that
// entry from ITS OWN color's list -- other colors untouched, per-color
// persistence preserved. Index-based (not url-based) so it is unambiguous
// when two entries share a url, and so it removes ORPHAN entries (images
// no longer in the Available pool -- a dropped SanMar image or a
// deleted-placement preview -- that have no tile to un-click). Positions
// are renumbered so they stay contiguous. Writes the WHOLE
// storefront_images map back through updateField (single write model).
function onRemoveGlobal(renderedIndex) {
    const item = lineupItems.value[renderedIndex]
    if (!item) return
    const color = item.color
    const current = storefrontImages.value ?? {}
    const existing = Array.isArray(current[color]) ? current[color] : []
    if (item.withinColorIndex < 0 || item.withinColorIndex >= existing.length) {
        return
    }
    const nextEntries = existing.filter((_, i) => i !== item.withinColorIndex)
    const next = renumberPositions(
        { ...current, [color]: nextEntries },
        pickerColorNames.value,
    )
    store.updateField('storefront_images', next)
}

const imageCaption = computed(() => {
    // Pool total + chosen total. "available" reflects poolTiles, which DOES
    // narrow under a specific Product Listing color filter (Jake
    // 2026-07-21, PR-B) -- so this count matches the visibly-filtered
    // pool grid. "chosen" reflects lineupItems, which is ALWAYS the full
    // cross-color total (Jake bug report, 2026-07-29): the Chosen lineup
    // never filters, so its count must not appear to shrink either.
    const poolCount = poolTiles.value.length
    if (poolCount === 0) {
        return '0 images — pick a SKU to load product images'
    }
    const chosenCount = lineupItems.value.length
    return `${poolCount} images available · ${chosenCount} chosen`
})

/* ============================================================
   Day 8 Item B2 - Quantity per shopper card.

   `quantity_per_shopper_override` is a JSONB column on
   CampaignProduct (see api/src/models/campaign_product.py:181).
   Shape: { min: number, max: number } when overridden, null
   when inheriting the store-level min_quantity / max_quantity.

   Inherited readout reads min_quantity + max_quantity directly off
   the CP - those are populated from the store-level defaults at
   campaign-product create time. Override writes/clears the JSONB.
   ============================================================ */

// Sensible fallbacks when the CP doesn't yet carry store-inherited
// values (newly-seeded CP). 1-99 matches the implementation plan.
const FALLBACK_INHERITED_MIN = 1
const FALLBACK_INHERITED_MAX = 99
const OVERRIDE_DEFAULT_MIN = 1
const OVERRIDE_DEFAULT_MAX = 5

const inheritedMin = computed(
    () =>
        store.getEffectiveAttribute('min_quantity')
        ?? FALLBACK_INHERITED_MIN,
)
const inheritedMax = computed(
    () =>
        store.getEffectiveAttribute('max_quantity')
        ?? FALLBACK_INHERITED_MAX,
)

const quantityOverride = computed(
    () => store.getEffectiveAttribute('quantity_per_shopper_override') ?? null,
)

function onToggleQuantityOverride(enabled) {
    if (enabled) {
        store.updateField('quantity_per_shopper_override', {
            min: OVERRIDE_DEFAULT_MIN,
            max: OVERRIDE_DEFAULT_MAX,
        })
    } else {
        store.updateField('quantity_per_shopper_override', null)
    }
}

function onUpdateOverrideMin(value) {
    const current = quantityOverride.value ?? {
        min: OVERRIDE_DEFAULT_MIN,
        max: OVERRIDE_DEFAULT_MAX,
    }
    const next = { ...current, min: coerceQty(value, current.min) }
    store.updateField('quantity_per_shopper_override', next)
}

function onUpdateOverrideMax(value) {
    const current = quantityOverride.value ?? {
        min: OVERRIDE_DEFAULT_MIN,
        max: OVERRIDE_DEFAULT_MAX,
    }
    const next = { ...current, max: coerceQty(value, current.max) }
    store.updateField('quantity_per_shopper_override', next)
}

// v-text-field type=number emits strings on some Vuetify versions; coerce
// safely so the JSONB column always carries a real integer. Fall back to
// the previous value on a non-finite input so we never poison the store
// with NaN.
function coerceQty(value, fallback) {
    if (value === '' || value == null) return fallback
    const n = Number(value)
    if (!Number.isFinite(n)) return fallback
    return Math.trunc(n)
}

/* ============================================================
   Day 8 Item B2 - Visibility card.

   `visible` is a bool column on CampaignProduct (added in migration
   des_exp_p1a_001). The legacy `hide_from_storefront` column is
   kept in sync server-side; clients only need to write `visible`.

   Default for a fresh CP is "visible" -- if the field is missing
   on serverState we treat it as true so the toggle reads on rather
   than presenting a misleading off-state during initial load.
   ============================================================ */

const isVisible = computed(() => {
    const v = store.getEffectiveAttribute('visible')
    if (v == null) return true
    return Boolean(v)
})

function onUpdateVisibility(value) {
    store.updateField('visible', Boolean(value))
}
</script>

<style scoped>
.storefront-header {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 12px;
}

.storefront-header-icon {
    flex: 0 0 auto;
    font-size: 28px;
}

.storefront-header-text {
    flex: 1 1 auto;
    min-width: 0;
}

.storefront-title {
    font-weight: 600;
    font-size: 16px;
    line-height: 1.2;
}

.storefront-subtitle {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.6);
    margin-top: 2px;
}

.storefront-fields {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;
}

.storefront-images-row {
    display: flex;
    flex-direction: row;
    gap: 16px;
    margin-top: 8px;
    margin-bottom: 12px;
}

.storefront-image-figure {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0;
    width: 96px;
}

.storefront-image-figure.is-clickable {
    cursor: pointer;
}

.storefront-image-img,
.storefront-image-figure.is-placeholder::before {
    display: block;
    width: 96px;
    height: 108px;
    object-fit: cover;
    background-color: #cfcfd2;
    border-radius: 4px;
}

.storefront-image-figure.is-placeholder::before {
    content: '';
}

.storefront-image-caption {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: rgba(0, 0, 0, 0.55);
    margin-top: 4px;
    text-transform: uppercase;
}

/* =====================================================================
   Product-image picker slice 1 -- pool + tiles.

   Finding 1 (2026-07-09 storefront-cleanup): these classes were used in
   the template but never defined, so pool <img>s rendered at their
   natural resolution (a SanMar model shot ~1000x1200 dwarfing an Artifi
   400x600 tile, stacked full-width). Define them as a wrapping thumbnail
   grid with uniform tile size, mirroring the lineup thumbnail language
   below (.storefront-picker-lineup-img is 44x50; the pool runs slightly
   larger at 72x84 since the pool is the primary browsing surface).
   ===================================================================== */
.storefront-picker {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
    margin-bottom: 12px;
}

.storefront-picker-color-row {
    margin-bottom: 4px;
}

.storefront-picker-heading {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.55);
    margin-top: 4px;
}

.storefront-picker-heading-hint {
    margin-left: 6px;
    font-weight: 400;
    letter-spacing: normal;
    text-transform: none;
    font-size: 11px;
    color: rgba(0, 0, 0, 0.45);
}

.storefront-picker-pool {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.storefront-picker-tile {
    position: relative;
    cursor: pointer;
    padding: 0;
    /* Keep a hairline edge so a white card is still distinguishable from
       the white panel; the selected state overrides the color. */
    border: 2px solid var(--tf-border, #e0e0e0);
    border-radius: 6px;
    overflow: hidden;
    background: #fff;
}

.storefront-picker-tile.is-selected {
    border-color: var(--tf-brand-primary, #119ae9);
}

.storefront-picker-tile-img {
    display: block;
    width: 72px;
    height: 84px;
    /* contain (not cover) so the WHOLE product image is visible, and a
       WHITE background (Jake 2026-07-15) so portrait SanMar shots have no
       grey letterbox bars -- the image sits on a clean white card, whole
       image visible, no grey edges (card efG02KCo). */
    object-fit: contain;
    background: #fff;
}

/* "Render all colors" backfill (Jake 2026-10-06). */
.storefront-picker-render-all {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 4px 0 8px;
    padding: 6px 12px;
    border: 1px solid var(--tf-brand-primary, #119ae9);
    border-radius: 6px;
    background: #fff;
    color: var(--tf-brand-primary, #119ae9);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}

.storefront-picker-render-all:hover {
    background: rgba(17, 154, 233, 0.08);
}

.storefront-picker-render-all-icon {
    font-size: 18px;
}

.storefront-picker-render-all-count {
    font-weight: 400;
}

/* A decorated render still generating for this color (Jake 2026-10-06).
   Same gradient + timing as the Stage's .stage-shimmer so "generating" reads
   identically across the editor; sized to match a real tile. */
.storefront-picker-tile--pending {
    cursor: default;
}

/* A render that dropped a placement (#312): a full-row notice. */
.storefront-picker-partial {
    flex-basis: 100%;
    grid-column: 1 / -1;
    font-size: 12px;
    line-height: 1.4;
    padding: 6px 10px;
    border-radius: 6px;
    background: rgba(var(--v-theme-warning), 0.12);
}

.storefront-picker-tile-shimmer {
    width: 72px;
    height: 84px;
}

/* Combined-picking (Jake 2026-07-15): each pool tile shows its ORIGIN color
   so the builder knows which color a shared/combined image will attach to. */
.storefront-picker-tile-color {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 1px 4px;
    font-size: 9px;
    font-weight: 600;
    line-height: 1.3;
    color: #fff;
    background: rgba(0, 0, 0, 0.55);
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    pointer-events: none;
}

.storefront-picker-tile-order {
    position: absolute;
    top: 4px;
    left: 4px;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    color: #fff;
    background: var(--tf-brand-primary, #119ae9);
    border-radius: 50%;
}

.storefront-picker-empty {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.55);
    padding: 8px 0;
}

/* Slice 3 -- Chosen (display order) drag-reorder GRID. Square-ish cards
   sit side by side and wrap to multiple rows; sortablejs owns the drag
   gesture. Order badge + remove (X) overlay each card; the whole card
   image fits (object-fit: contain, no cropping). */
.storefront-picker-lineup {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 8px;
    margin-top: 12px;
}

.storefront-picker-lineup-item {
    position: relative;
    width: 96px;
    height: 96px;
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 8px;
    /* WHITE card (Jake 2026-07-15) -- no grey behind the image. */
    background: #fff;
    overflow: hidden;
    cursor: grab;
}

.storefront-picker-lineup-item:active {
    cursor: grabbing;
}

/* De-selected-color chosen image (Jake, card q7EQ3a0b): the image belongs
   to a color no longer selected in Sizes & Colors. Surfaced (never pruned)
   with an amber card border + a compact chip above the color-name strip
   so the builder can SEE the association. The garment color itself is NOT
   dimmed (2026-07-29 -- dimming made the color hard to judge, defeating the
   point of a color-swatch card). The badge carries VISIBLE text (not
   color-only) for accessibility. */
.storefront-picker-lineup-item.is-deselected {
    border-color: #efc879;
}

.storefront-picker-lineup-deselected {
    position: absolute;
    bottom: 19px;
    left: 0;
    right: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 1px 3px;
    font-size: 8px;
    font-weight: 600;
    line-height: 1.2;
    color: #6a5008;
    background: #fffaf0;
    border-top: 1px solid #efc879;
    border-bottom: 1px solid #efc879;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    pointer-events: none;
}

.storefront-picker-lineup-deselected-icon {
    font-size: 10px;
    color: #b26a00;
    flex-shrink: 0;
}

/* Chosen card origin-color label (combined lineup spans all colors). */
.storefront-picker-lineup-color {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1;
    padding: 2px 4px;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.3;
    color: #fff;
    background: rgba(0, 0, 0, 0.55);
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    pointer-events: none;
}

/* Drag-ghost fix (Jake 2026-07-15) -- reads as picking up ONE card, not a
   duplicate. The GHOST is the placeholder left in the source slot: render
   it as a faint dashed drop-target with its contents hidden, so there is
   never a second visible copy of the image. The CHOSEN element (being
   picked up) and the DRAG element (following the cursor) get a lifted look. */
.storefront-picker-lineup-item.picker-drag-ghost {
    opacity: 0.4;
    background: #f0f4f8;
    border: 2px dashed var(--tf-brand-primary, #119ae9);
}

.storefront-picker-lineup-item.picker-drag-ghost > * {
    visibility: hidden;
}

.storefront-picker-lineup-item.picker-drag-chosen {
    cursor: grabbing;
}

.storefront-picker-lineup-item.picker-drag-active {
    opacity: 1;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.28);
    transform: scale(1.03);
}

.storefront-picker-lineup-order {
    position: absolute;
    top: 4px;
    left: 4px;
    z-index: 1;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    color: #fff;
    background: var(--tf-brand-primary, #119ae9);
    border-radius: 50%;
}

.storefront-picker-lineup-img {
    display: block;
    width: 96px;
    height: 96px;
    /* contain so portrait SanMar shots and square Artifi tiles both fit
       whole, on a WHITE background (Jake 2026-07-15) so there are no grey
       letterbox bars -- clean white card, whole image visible. */
    object-fit: contain;
    background: #fff;
}

.storefront-picker-remove {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 1;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    line-height: 1;
    color: #fff;
    background: rgba(0, 0, 0, 0.55);
    border: none;
    border-radius: 50%;
    cursor: pointer;
}

.storefront-picker-remove:hover {
    background: rgba(0, 0, 0, 0.75);
}

.storefront-picker-remove :deep(.v-icon),
.storefront-picker-remove :deep(i) {
    font-size: 14px;
}

/* "Set as default" (Jake's decision B). A small, unobtrusive star toggle
   in the opposite corner from remove (X) so the two never collide. */
.storefront-picker-set-default {
    position: absolute;
    top: 4px;
    left: 4px;
    z-index: 1;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    line-height: 1;
    color: #fff;
    background: rgba(0, 0, 0, 0.55);
    border: none;
    border-radius: 50%;
    cursor: pointer;
}

.storefront-picker-set-default:hover {
    background: rgba(0, 0, 0, 0.75);
}

.storefront-picker-set-default.is-active {
    background: var(--tf-brand-primary, #119ae9);
}

.storefront-picker-set-default :deep(.v-icon),
.storefront-picker-set-default :deep(i) {
    font-size: 14px;
}

/* The active default card gets a distinct border so it is obvious which
   chosen image is currently the storefront default. */
.storefront-picker-lineup-item.is-default {
    border-color: var(--tf-brand-primary, #119ae9);
    border-width: 2px;
}

.storefront-picker-is-default-badge {
    position: absolute;
    bottom: 15px;
    left: 0;
    right: 0;
    z-index: 1;
    padding: 1px 4px;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #fff;
    text-align: center;
    background: var(--tf-brand-primary, #119ae9);
    pointer-events: none;
}

/* The rare case where a chosen image's color is BOTH the default AND
   de-selected from Sizes & Colors -- stack the Default badge above the
   de-selected chip rather than overlapping it. */
.storefront-picker-is-default-badge.stacks-above-deselected {
    bottom: 30px;
}

.storefront-image-status {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
    color: rgba(0, 0, 0, 0.6);
    font-size: 12px;
}

.storefront-image-status-icon {
    font-size: 16px;
}

.storefront-image-status-text {
    line-height: 1.3;
}

/* =====================================================================
   Day 8 Item B2 - Quantity per shopper + Visibility cards.
   Spacing/colors track the 2026-06-13 design bundle (editor-a.css).
   ===================================================================== */

.storefront-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.storefront-followup-card {
    margin-top: 0;
}

.storefront-qty-inherited {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 13px;
    color: #5a6a82;
    background: #f5f8fa;
    border-radius: 8px;
    padding: 10px 13px;
}

.storefront-qty-inherited-icon {
    font-size: 16px;
    color: #5a6a82;
}

.storefront-qty-inherited strong {
    color: #212121;
    font-weight: 600;
}

.storefront-toggle-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 10px;
}

.storefront-toggle-label {
    font-size: 13px;
    color: #212121;
    line-height: 1.3;
}

.storefront-toggle-label small {
    display: block;
    font-size: 11px;
    color: rgba(0, 0, 0, 0.55);
}

.storefront-qty-override {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
    padding: 10px 13px;
    background: #fffaf0;
    border: 1px solid #efc879;
    border-radius: 8px;
    color: #6a5008;
    font-size: 13px;
}

.storefront-qty-override-icon {
    color: #b26a00;
    font-size: 16px;
}

.storefront-qty-input {
    max-width: 110px;
}

.storefront-qty-separator,
.storefront-qty-suffix {
    color: #6a5008;
    font-size: 12px;
}

.storefront-visibility-note {
    margin-top: 12px;
    padding: 10px 13px;
    background: #f5f8fa;
    border-radius: 8px;
    font-size: 12px;
    color: #5a6a82;
    line-height: 1.4;
}
</style>
